import { MenuOutlined } from "@ant-design/icons";
import { Button, Drawer, Dropdown, Layout, Menu } from "antd";
import type { MenuProps } from "antd";
import { useMemo, useRef, useState, useEffect } from "react";
import { Link, useLocation } from "react-router";

import productPreviewImage from "@/assets/images/product-detail/1.png";
import { DropdownIcon } from "@/components/icons/Dropdown";

import { Logo } from "@/components/Logo";

import { navProducts, navSupport } from "@/data/siteContent";
import { useT } from "@/i18n";
import { pathNames } from "@/router/paths";

const { Header: AntHeader } = Layout;

const navTriggerClass =
  "text-[22px]! font-bold! text-yoyo-text  hover:!bg-transparent px-6 py-2 hover:!text-[#666666]";

const navTriggerClassTransparent =
  "text-[22px]! font-bold! text-white  hover:!bg-transparent px-6 py-2 hover:!text-[#666666]";

function buildLinkItems(
  items: { label: string; path: string }[],
): MenuProps["items"] {
  return items.map((item) => ({
    key: item.path,
    label: (
      <Link
        to={item.path}
        style={{
          fontSize: "18px",
          padding: "0px 24px 0px 0px",
          display: "block",
        }}
      >
        {item.label}
      </Link>
    ),
  }));
}

export function Header() {
  const lt = useT();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [productsMenuOpen, setProductsMenuOpen] = useState(false);
  const [supportMenuOpen, setSupportMenuOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const isInMenuZoneRef = useRef(false);
  const [menuCloseTimeout, setMenuCloseTimeout] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);

  const isHomePage = location.pathname === pathNames.home;

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollOpacity = isHovering ? 1 : Math.min(scrollY / 200, 1);
  const isHeaderWhite =
    !isHomePage ||
    isHovering ||
    productsMenuOpen ||
    supportMenuOpen ||
    scrollY > 10;

  const enterMenuZone = () => {
    isInMenuZoneRef.current = true;
    if (menuCloseTimeout) {
      clearTimeout(menuCloseTimeout);
      setMenuCloseTimeout(null);
    }
    setProductsMenuOpen(true);
  };

  const leaveMenuZone = () => {
    isInMenuZoneRef.current = false;
    if (menuCloseTimeout) {
      clearTimeout(menuCloseTimeout);
    }
    const timeout = setTimeout(() => {
      if (!isInMenuZoneRef.current) {
        setProductsMenuOpen(false);
      }
      setMenuCloseTimeout(null);
    }, 200);
    setMenuCloseTimeout(timeout);
  };

  const productsItems = useMemo(
    () =>
      navProducts.map((item) => ({
        label: lt(item.labelKey),
        path: item.path,
      })),
    [lt],
  );

  const supportItems = useMemo(
    () =>
      navSupport.map((item) => ({
        label: lt(item.labelKey),
        path: item.path,
      })),
    [lt],
  );

  const dropdownMenuStyle = {
    fontSize: "22px",
    padding: "8px 0",
  };

  const supportMenu: MenuProps = {
    items: buildLinkItems(supportItems),
    style: dropdownMenuStyle,
  };

  const mobileMenuItems: MenuProps["items"] = [
    {
      key: "products",
      label: lt("nav.products"),
      children: buildLinkItems(productsItems),
    },
    {
      key: "support",
      label: lt("nav.support"),
      children: buildLinkItems(supportItems),
    },
    {
      key: pathNames.about,
      label: <Link to={pathNames.about}>{lt("nav.about")}</Link>,
    },
  ];

  const headerClassName = `z-40 !h-auto !px-0 !leading-normal transition-all duration-300 ${
    isHomePage
      ? "fixed top-0 left-0 right-0 border-b border-transparent"
      : "sticky top-0 border-b border-[#f1f1f1]"
  }`;

  const headerStyle = isHomePage
    ? {
        backgroundColor: `rgba(255, 255, 255, ${scrollOpacity})`,
        borderBottom:
          scrollOpacity > 0 ? "1px solid #f1f1f1" : "1px solid transparent",
      }
    : {
        backgroundColor: "white",
        borderBottom: "1px solid #f1f1f1",
      };

  const currentNavTriggerClass = isHeaderWhite
    ? navTriggerClass
    : navTriggerClassTransparent;

  return (
    <AntHeader
      className={headerClassName}
      style={headerStyle}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="relative flex max-w-site mx-auto items-center justify-between lg:px-8 md:px-6 h-22.5 px-4">
        <Logo />

        <nav className="hidden items-center md:flex gap-10">
          <div
            className="relative"
            onMouseEnter={enterMenuZone}
            onMouseLeave={leaveMenuZone}
          >
            <Button
              type="text"
              className={`${currentNavTriggerClass} relative`}
            >
              {lt("nav.products")}
              <DropdownIcon
                className={`transition-transform duration-300 absolute top-[16px] right-[-4px] -translate-y-1/2 ${productsMenuOpen ? "" : "rotate-180"}`}
              />
            </Button>

            {productsMenuOpen && (
              <div
                className="fixed left-0 right-0 top-[91px] h-[500px] bg-white shadow-lg flex flex-col items-center justify-center z-50 animate-slide-down"
                onMouseEnter={enterMenuZone}
                onMouseLeave={leaveMenuZone}
              >
                <Link
                  to="/products/yoyo-gen1"
                  className="cursor-pointer"
                  onClick={() => setProductsMenuOpen(false)}
                >
                  <div className="w-[320px] h-[320px]    mb-6">
                    <img src={productPreviewImage} className="rounded-[20px]" />
                  </div>
                  <div className="text-center text-xl font-bold text-[#131313] relative -left-4">
                    yoyo Parent-Child Companion Robot
                  </div>
                </Link>
              </div>
            )}
          </div>

          <Dropdown
            menu={supportMenu}
            trigger={["hover"]}
            overlayClassName="header-dropdown-menu"
            open={supportMenuOpen}
            onOpenChange={(open) => setSupportMenuOpen(open)}
            getPopupContainer={() => document.body}
          >
            <Button type="text" className={`${currentNavTriggerClass}`}>
              {lt("nav.support")}
              <DropdownIcon
                className={`transition-transform duration-300 absolute top-[16px] right-[-4px] -translate-y-1/2 ${supportMenuOpen ? "" : "rotate-180"}`}
              />
            </Button>
          </Dropdown>
          <Link to={pathNames.about}>
            <Button type="text" className={`${currentNavTriggerClass}`}>
              {lt("nav.about")}
            </Button>
          </Link>
        </nav>

        <div className="absolute right-0 flex items-center gap-1">
          <Button
            type="text"
            icon={<MenuOutlined />}
            className={`!inline-flex md:!hidden ${isHeaderWhite ? "text-yoyo-text" : "text-white"}`}
            aria-label="Menu"
            onClick={() => setMobileOpen(true)}
          />
        </div>
      </div>

      <Drawer
        title={lt("common.menu")}
        placement="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        width={280}
        styles={{ body: { padding: 0 } }}
      >
        <Menu
          mode="inline"
          items={mobileMenuItems}
          onClick={() => setMobileOpen(false)}
          style={{ border: "none" }}
        />
      </Drawer>
    </AntHeader>
  );
}
